/**
 * The device's durable storage — IndexedDB via Dexie.
 *
 * This file is the reason the sync core is framework-free: everything above it talks to
 * `OutboxStore` and `PhotoBlobStore`, and this is the one place that knows what a browser
 * database is. If ADR-0001 §4.8's escape hatch is ever taken and the shell becomes React
 * Native, this file is rewritten and nothing else is.
 *
 * **Everything in here is holding the only copy of somebody's evidence.** A photo taken on
 * a roof exists in exactly one place — this database — until the server confirms it. That
 * shapes three decisions worth naming:
 *
 *  1. **No cascading deletes, no cleanup jobs, no "clear old data" convenience.** The only
 *     delete in this file is `forget`, and it releases photo bytes that object storage has
 *     already confirmed against a matching checksum.
 *  2. **Writes that must happen together happen in one Dexie transaction.** IndexedDB
 *     gives us real transactions; a phone dying mid-capture is an ordinary event, not an
 *     edge case.
 *  3. **Schema upgrades are additive.** A crew is running whatever build they installed
 *     last week and may have unsynced ops in an older shape. A migration that drops a
 *     column drops evidence.
 */

import Dexie, { type EntityTable, type Transaction } from 'dexie';

import type { OpError, OutboxRecord, OutboxStatus, OutboxStore, SyncOp, Uuid } from '@rush/sync-core';
import type { PhotoBlobStore } from '@rush/sync-core';

/**
 * The stored shape of an outbox row.
 *
 * Flattened rather than `{ op, ...meta }` because IndexedDB can only index top-level
 * properties, and `listSendable` has to filter on status and order by localSeq. The op
 * itself is stored whole and untouched — retries resend exactly these bytes.
 */
interface OutboxRow {
  /** Primary key. The server's idempotency key too, so it is unique by construction. */
  opId: Uuid;
  localSeq: number;
  status: OutboxStatus;
  attemptCount: number;
  nextAttemptAt: number;
  /** Duplicated out of `op` so per-entity FIFO can be evaluated without deserialising. */
  entityId: Uuid;
  serverSeq?: number;
  lastError?: OpError;
  op: SyncOp;
}

interface QuarantineRow {
  entityId: Uuid;
  reason: OpError;
  quarantinedAt: number;
}

interface PhotoBlobRow {
  photoId: Uuid;
  bytes: Uint8Array;
  contentType: string;
  byteLength: number;
  storedAt: number;
}

/**
 * Local read model — what the screen renders from.
 *
 * Kept separate from the outbox on purpose. The outbox is a log of things we owe the
 * server; this is the crew's current picture of the job. Deriving the screen from the
 * outbox would mean a synced-and-pruned op erases a photo from the rep's view, and it
 * would put UI queries on the critical path of the sync engine.
 *
 * Rows here are written in the *same transaction* as their op. Either the crew's screen
 * and our obligation to the server both record a photo, or neither does.
 */
interface JobRow {
  jobId: Uuid;
  orgId: Uuid;
  kind: string;
  address: string;
  homeownerName: string;
  homeownerPhone: string;
  dateOfLoss: string;
  carrier: string;
  claimNumber: string;
  createdAt: number;
}

interface PhotoRow {
  photoId: Uuid;
  jobId: Uuid;
  slotId: string;
  sha256: string;
  byteLength: number;
  contentType: string;
  /** The device's belief about when the shutter closed. Displayed as "captured at". */
  capturedAt: string;
  /** Set by a `redact` op. The row and the bytes stay; it stops satisfying a slot. */
  redacted: boolean;
}

/** Single-row table holding the local sequence counter. See `#nextLocalSeq`. */
interface CounterRow {
  key: string;
  value: number;
}

export class RushDatabase extends Dexie {
  outbox!: EntityTable<OutboxRow, 'opId'>;
  quarantine!: EntityTable<QuarantineRow, 'entityId'>;
  photoBlobs!: EntityTable<PhotoBlobRow, 'photoId'>;
  counters!: EntityTable<CounterRow, 'key'>;
  jobs!: EntityTable<JobRow, 'jobId'>;
  photos!: EntityTable<PhotoRow, 'photoId'>;

  constructor(name = 'rush-field') {
    super(name);

    // Version 1. Add tables and indexes in later versions; never remove a field that an
    // unsynced op might still be using.
    this.version(1).stores({
      // `status` and `nextAttemptAt` are compound-indexed because listSendable always
      // filters on both, and a full scan on a phone with a week of queued work is slow
      // enough for a rep to notice.
      outbox: 'opId, localSeq, status, entityId, [status+localSeq], [status+nextAttemptAt]',
      quarantine: 'entityId',
      photoBlobs: 'photoId, storedAt',
      counters: 'key',
      jobs: 'jobId, createdAt',
      photos: 'photoId, jobId, [jobId+slotId]',
    });
  }
}

const LOCAL_SEQ_KEY = 'outbox.localSeq';

/**
 * Dexie-backed outbox.
 *
 * Passes the same `describeOutboxStoreContract` suite as the in-memory reference. That is
 * not a nicety: every guarantee `OutboxDrainer` is proven to hold is proven against the
 * in-memory store, so those proofs only transfer to the device if the two stores behave
 * identically.
 */
export class DexieOutboxStore implements OutboxStore {
  readonly #db: RushDatabase;

  constructor(db: RushDatabase) {
    this.#db = db;
  }

  async enqueue(op: SyncOp): Promise<OutboxRecord> {
    return await this.#db.transaction('rw', this.#db.outbox, this.#db.counters, async () => {
      // The uniqueness check is inside the transaction so two concurrent captures cannot
      // both pass it. A duplicate opId would be deduped by the server, but locally it
      // would mean one of the two photos has no op at all.
      const existing = await this.#db.outbox.get(op.opId);
      if (existing !== undefined) throw new Error(`duplicate opId enqueued: ${op.opId}`);

      const localSeq = await this.#nextLocalSeq();
      const row: OutboxRow = {
        opId: op.opId,
        localSeq,
        status: 'pending',
        attemptCount: 0,
        nextAttemptAt: 0,
        entityId: op.entityId,
        op,
      };
      await this.#db.outbox.add(row);
      return toRecord(row);
    });
  }

  /**
   * Oldest-first pending ops, excluding quarantined entities and anything still backing
   * off, and stopping an entity at its first ineligible op.
   *
   * Reads all pending rows rather than paging in the index. The queue is bounded by what
   * one crew captures between syncs — hundreds of ops, not millions — and the per-entity
   * blocking rule cannot be expressed as an index range: whether an op is eligible depends
   * on the op ahead of it. Doing this correctly in memory beats doing it cleverly and
   * wrong, and the ordering bug it would cause writes evidence against a nonexistent base.
   */
  async listSendable(now: number, limit: number): Promise<readonly OutboxRecord[]> {
    return await this.#db.transaction('r', this.#db.outbox, this.#db.quarantine, async () => {
      const pending = await this.#db.outbox
        .where('[status+localSeq]')
        .between(['pending', Dexie.minKey], ['pending', Dexie.maxKey])
        .toArray();

      const quarantined = new Set(await this.#db.quarantine.toCollection().primaryKeys());

      const blocked = new Set<Uuid>();
      const sendable: OutboxRecord[] = [];

      for (const row of pending) {
        if (sendable.length >= limit) break;
        if (quarantined.has(row.entityId)) continue;
        if (blocked.has(row.entityId)) continue;
        if (row.nextAttemptAt > now) {
          // This entity's earliest eligible op is not ready, so nothing later for the same
          // entity may go out ahead of it.
          blocked.add(row.entityId);
          continue;
        }
        sendable.push(toRecord(row));
      }

      return sendable;
    });
  }

  async markSynced(opId: Uuid, serverSeq: number): Promise<void> {
    await this.#update(opId, (row) => ({ ...row, status: 'synced', serverSeq }));
  }

  async markRetry(opId: Uuid, nextAttemptAt: number, error: OpError): Promise<void> {
    await this.#update(opId, (row) => ({
      ...row,
      status: 'pending',
      attemptCount: row.attemptCount + 1,
      nextAttemptAt,
      lastError: error,
    }));
  }

  async markFailed(opId: Uuid, error: OpError): Promise<void> {
    await this.#update(opId, (row) => ({
      ...row,
      status: 'failed',
      attemptCount: row.attemptCount + 1,
      lastError: error,
    }));
  }

  async quarantineEntity(entityId: Uuid, reason: OpError): Promise<void> {
    // `put`, not `add`: re-quarantining an already-frozen entity is a normal outcome of a
    // retry and must not throw.
    await this.#db.quarantine.put({ entityId, reason, quarantinedAt: Date.now() });
  }

  async countUnsynced(): Promise<number> {
    // Everything that is not `synced` — pending *and* failed. A failed op is unfinished
    // business sitting on a phone; counting it as done is how a crew gets told "0 pending"
    // over evidence that will never leave the device.
    const total = await this.#db.outbox.count();
    const synced = await this.#db.outbox.where('status').equals('synced').count();
    return total - synced;
  }

  // -- reads the UI needs, beyond the port ----------------------------------

  /** Oldest unsynced op's local sequence age, for the "N waiting since…" indicator. */
  async oldestUnsyncedAt(): Promise<number | undefined> {
    const row = await this.#db.outbox
      .where('[status+localSeq]')
      .between(['pending', Dexie.minKey], ['pending', Dexie.maxKey])
      .first();
    return row === undefined ? undefined : Date.parse(row.op.clientCreatedAt);
  }

  async countByStatus(): Promise<Record<OutboxStatus, number>> {
    const [pending, synced, failed] = await Promise.all([
      this.#db.outbox.where('status').equals('pending').count(),
      this.#db.outbox.where('status').equals('synced').count(),
      this.#db.outbox.where('status').equals('failed').count(),
    ]);
    return { pending, synced, failed };
  }

  /**
   * Monotonic local sequence.
   *
   * A counter row rather than Dexie's `++` auto-increment, because the primary key has to
   * be `opId` (it is what every other call looks a row up by) and auto-increment is only
   * available on the primary key. Callers hold a `rw` transaction over `counters`, which
   * is what makes the read-modify-write atomic.
   */
  async #nextLocalSeq(): Promise<number> {
    const current = await this.#db.counters.get(LOCAL_SEQ_KEY);
    const next = (current?.value ?? 0) + 1;
    await this.#db.counters.put({ key: LOCAL_SEQ_KEY, value: next });
    return next;
  }

  async #update(opId: Uuid, fn: (row: OutboxRow) => OutboxRow): Promise<void> {
    await this.#db.transaction('rw', this.#db.outbox, async () => {
      const row = await this.#db.outbox.get(opId);
      if (row === undefined) throw new Error(`unknown opId: ${opId}`);
      await this.#db.outbox.put(fn(row));
    });
  }
}

function toRecord(row: OutboxRow): OutboxRecord {
  // Rebuilt explicitly rather than spread, so a new stored column cannot leak into the
  // port's shape and quietly become something callers depend on.
  // The optional fields are spread in only when present rather than set to `undefined`.
  // Under `exactOptionalPropertyTypes` those are different things, and the distinction is
  // a real one here: `{ serverSeq: undefined }` claims the key exists and is empty, which
  // for a confirmed-by-the-server field is a different assertion than "not confirmed".
  return {
    op: row.op,
    localSeq: row.localSeq,
    status: row.status,
    attemptCount: row.attemptCount,
    nextAttemptAt: row.nextAttemptAt,
    ...(row.serverSeq === undefined ? {} : { serverSeq: row.serverSeq }),
    ...(row.lastError === undefined ? {} : { lastError: row.lastError }),
  };
}

/**
 * Dexie-backed photo bytes.
 *
 * Separate table from the outbox because the two have different lifetimes: an op is
 * released on server confirmation, bytes only once object storage has them and the
 * checksum matched on arrival.
 */
export class DexiePhotoBlobStore implements PhotoBlobStore {
  readonly #db: RushDatabase;

  constructor(db: RushDatabase) {
    this.#db = db;
  }

  async put(photoId: Uuid, bytes: Uint8Array, contentType: string): Promise<void> {
    // Copy before storing. The caller's buffer may be a view into a larger one owned by
    // the file reader, and IndexedDB would clone the whole backing buffer.
    const copy = new Uint8Array(bytes);
    await this.#db.photoBlobs.put({
      photoId,
      bytes: copy,
      contentType,
      byteLength: copy.byteLength,
      storedAt: Date.now(),
    });
  }

  async get(photoId: Uuid): Promise<Uint8Array | undefined> {
    const row = await this.#db.photoBlobs.get(photoId);
    return row === undefined ? undefined : new Uint8Array(row.bytes);
  }

  async forget(photoId: Uuid): Promise<void> {
    await this.#db.photoBlobs.delete(photoId);
  }

  async listPendingUpload(): Promise<readonly Uuid[]> {
    // Held locally means not yet confirmed remotely — `forget` is the only thing that
    // removes a row, and it is only called after a verified upload.
    return (await this.#db.photoBlobs.orderBy('storedAt').primaryKeys()) as Uuid[];
  }

  async totalBytes(): Promise<number> {
    let total = 0;
    await this.#db.photoBlobs.each((row) => {
      total += row.byteLength;
    });
    return total;
  }
}

/** Opens the database and returns both stores over it. */
export function openRushDatabase(name = 'rush-field'): {
  db: RushDatabase;
  outbox: DexieOutboxStore;
  blobs: DexiePhotoBlobStore;
} {
  const db = new RushDatabase(name);
  return { db, outbox: new DexieOutboxStore(db), blobs: new DexiePhotoBlobStore(db) };
}

export type { JobRow, OutboxRow, PhotoBlobRow, PhotoRow, Transaction };
